import { useEffect, useState } from "react";
import type { FilterColor } from "../../../shared/types";
import {
  useSaveSenderFilter,
  useSaveSenderGroup,
  useSenderGroups,
} from "../../hooks/useApi";
import { FILTER_COLORS, SWATCH_CLASSES } from "../filterColors";

const inputClass =
  "w-full rounded border border-stroke px-2.5 py-1.5 text-[13px] outline-none focus:border-accent";
const labelClass = "block pb-1 pt-2 text-[12px] font-medium text-ink-muted";

/**
 * Create a sender-filter group or a sender-filter. Opened from the sidebar "+"
 * (group mode) or an email's filter icon (filter mode, pre-filled with sender).
 */
export function CreateFilterDialog({
  mode,
  prefillAddress,
  onClose,
  onCreated,
}: {
  mode: "group" | "filter";
  prefillAddress?: string;
  onClose: () => void;
  onCreated: (filterId?: number) => void;
}) {
  const { data: groups } = useSenderGroups();
  const saveGroup = useSaveSenderGroup();
  const saveFilter = useSaveSenderFilter();

  const [name, setName] = useState(mode === "filter" && prefillAddress ? senderLabel(prefillAddress) : "");
  const [groupId, setGroupId] = useState<number | "new" | null>(null);
  const [newGroupName, setNewGroupName] = useState("From Email");
  const [addresses, setAddresses] = useState(prefillAddress ?? "");
  const [color, setColor] = useState<FilterColor>("blue");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const effectiveGroup = groupId ?? (groups && groups.length ? groups[0]!.id : "new");
  const busy = saveGroup.isPending || saveFilter.isPending;

  const submit = async () => {
    setError(null);
    try {
      if (mode === "group") {
        if (!name.trim()) return setError("Enter a group name.");
        await saveGroup.mutateAsync({ name: name.trim() });
        onCreated();
        return;
      }
      // Filter mode.
      const addrList = addresses
        .split(/[\n,]/)
        .map((a) => a.trim())
        .filter(Boolean);
      if (!name.trim()) return setError("Enter a filter name.");
      if (!addrList.length) return setError("Enter at least one email address.");

      let targetGroup = effectiveGroup;
      if (targetGroup === "new") {
        if (!newGroupName.trim()) return setError("Enter a name for the new group.");
        const g = await saveGroup.mutateAsync({ name: newGroupName.trim() });
        targetGroup = g.id;
      }
      const created = await saveFilter.mutateAsync({
        groupId: targetGroup as number,
        name: name.trim(),
        addresses: addrList,
        color,
      });
      onCreated(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/30" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="w-[440px] max-w-[92vw] rounded-lg border border-stroke bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={mode === "group" ? "New filter group" : "New filter"}
      >
        <h2 className="text-[15px] font-semibold">
          {mode === "group" ? "New filter group" : "New filter"}
        </h2>

        {mode === "group" ? (
          <>
            <label className={labelClass}>Group name</label>
            <input
              className={inputClass}
              autoFocus
              placeholder="From Email"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </>
        ) : (
          <>
            <label className={labelClass}>Group</label>
            <select
              className={inputClass}
              value={String(effectiveGroup)}
              onChange={(e) =>
                setGroupId(e.target.value === "new" ? "new" : Number(e.target.value))
              }
            >
              {(groups ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
              <option value="new">＋ New group…</option>
            </select>
            {effectiveGroup === "new" && (
              <input
                className={`${inputClass} mt-1.5`}
                placeholder="New group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            )}

            <label className={labelClass}>Filter name</label>
            <input
              className={inputClass}
              autoFocus
              placeholder="Teknikkdeler"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <label className={labelClass}>Sender address(es) — one per line</label>
            <textarea
              className={`${inputClass} h-16 resize-y`}
              placeholder="noreply@teknikkdeler.no"
              value={addresses}
              onChange={(e) => setAddresses(e.target.value)}
            />

            <label className={labelClass}>Color</label>
            <div className="flex items-center gap-1 pb-1.5">
              {FILTER_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  title={c}
                  aria-label={`Color ${c}`}
                  className={`size-4 rounded-full ${SWATCH_CLASSES[c]} ${
                    color === c ? "ring-2 ring-accent ring-offset-1" : ""
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {error && <p className="pt-2 text-[12px] text-red-700">{error}</p>}

        <div className="flex justify-end gap-2 pt-4">
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="rounded bg-accent px-4 py-1.5 text-[13px] font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy ? "Saving…" : mode === "group" ? "Create group" : "Create filter"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** "noreply@teknikkdeler.no" → "Teknikkdeler" (a friendly default filter name). */
export function senderLabel(address: string): string {
  const domain = address.split("@")[1] ?? address;
  const core = domain.split(".")[0] ?? domain;
  return core ? core.charAt(0).toUpperCase() + core.slice(1) : address;
}
